import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, TrendingUp, CheckCircle2, XCircle, ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '@/services/apiClient';

const TYPE_ICON = {
    trial_expiring_soon: { Icon: AlertTriangle, cls: 'text-amber-500' },
    grace_period_started: { Icon: AlertTriangle, cls: 'text-orange-500' },
    subscription_expired: { Icon: XCircle, cls: 'text-destructive' },
    upgrade_requested: { Icon: TrendingUp, cls: 'text-primary' },
    plan_upgraded: { Icon: CheckCircle2, cls: 'text-green-500' },
    addon_requested: { Icon: TrendingUp, cls: 'text-teal-500' },
    addon_applied: { Icon: CheckCircle2, cls: 'text-teal-500' },
};

function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [markingAll, setMarkingAll] = useState(false);
    const [panelPos, setPanelPos] = useState(null);
    const panelRef = useRef(null);
    const buttonRef = useRef(null);

    const fetchNotifications = useCallback(() => {
        getNotifications()
            .then(res => {
                setNotifications(res.data || []);
                setUnreadCount(res.unreadCount || 0);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        fetchNotifications();
        const id = setInterval(fetchNotifications, 60000);
        return () => clearInterval(id);
    }, [fetchNotifications]);

    const updatePanelPos = useCallback(() => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        setPanelPos({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right,
        });
    }, []);

    useEffect(() => {
        if (!open) {
            setPanelPos(null);
            return;
        }
        updatePanelPos();
        window.addEventListener('resize', updatePanelPos);
        window.addEventListener('scroll', updatePanelPos, true);
        return () => {
            window.removeEventListener('resize', updatePanelPos);
            window.removeEventListener('scroll', updatePanelPos, true);
        };
    }, [open, updatePanelPos]);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (
                panelRef.current?.contains(e.target) ||
                buttonRef.current?.contains(e.target)
            ) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleNotificationClick = async (n) => {
        if (!n.isRead) {
            await markNotificationRead(n._id).catch(() => {});
            setNotifications(prev => prev.map(x => x._id === n._id ? { ...x, isRead: true } : x));
            setUnreadCount(c => Math.max(0, c - 1));
        }
        if (n.data?.companyId) {
            navigate(`/super-admin/companies/${n.data.companyId}`);
        }
        setOpen(false);
    };

    const handleMarkAll = async () => {
        setMarkingAll(true);
        try {
            await markAllNotificationsRead();
            setNotifications(prev => prev.map(x => ({ ...x, isRead: true })));
            setUnreadCount(0);
        } finally {
            setMarkingAll(false);
        }
    };

    const displayed = notifications.slice(0, 10);
    const badgeLabel = unreadCount > 9 ? '9+' : unreadCount;

    const dropdownPanel = open && panelPos ? (
                <div
                    ref={panelRef}
                    style={{ top: panelPos.top, right: panelPos.right }}
                    className="fixed z-[100] w-80 rounded-xl border border-border bg-card shadow-lg"
                >
                    <div className="flex items-center justify-between border-b border-border px-4 py-3">
                        <span className="font-semibold text-foreground text-sm">Notifications</span>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 gap-1 text-xs"
                                    onClick={handleMarkAll}
                                    disabled={markingAll}
                                >
                                    <Check className="h-3.5 w-3.5" />
                                    Mark all read
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="max-h-[360px] overflow-y-auto">
                        {displayed.length === 0 ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">All caught up!</p>
                        ) : (
                            displayed.map(n => {
                                const ti = TYPE_ICON[n.type] || TYPE_ICON.upgrade_requested;
                                const Icon = ti.Icon;
                                return (
                                    <button
                                        key={n._id}
                                        type="button"
                                        onClick={() => handleNotificationClick(n)}
                                        className={`flex w-full items-start gap-3 border-b border-border/50 px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                                            n.isRead ? 'opacity-60' : ''
                                        }`}
                                    >
                                        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${ti.cls}`} />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-semibold text-foreground">{n.title}</p>
                                            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.message}</p>
                                            <p className="mt-1 text-[10px] text-muted-foreground/70">{timeAgo(n.createdAt)}</p>
                                        </div>
                                        {!n.isRead && (
                                            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>

                    <div className="border-t border-border px-4 py-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-full gap-1 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => { navigate('/super-admin/notifications'); setOpen(false); }}
                        >
                            View all notifications
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                </div>
    ) : null;

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setOpen(o => !o)}
                className="relative flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
            >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-0.5 text-[10px] font-bold text-white">
                        {badgeLabel}
                    </span>
                )}
            </button>

            {dropdownPanel && createPortal(dropdownPanel, document.body)}
        </div>
    );
}
